import React, { useState, useEffect, useRef } from 'react';
import Footer from './shared/Footer';
import { useLocation, useNavigate } from 'react-router-dom';
import config, { PRICING, calculateGstComponents, isExtremelyDiscounted } from '../config';
import { Box, Container, Paper, Typography, Button, CircularProgress, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchWithAuth } from '../utils/apiAuth';
import { trackPixelPurchase } from '../utils/googleAnalytics';

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const paymentData = location.state || {};
  const [isGeneratingReceipt, setIsGeneratingReceipt] = useState(false);
  const [receiptSaved, setReceiptSaved] = useState(false);
  const [receiptError, setReceiptError] = useState(null);
  const purchaseTrackedRef = useRef(false);
  
  // GST Constants from configuration
  const GST_NUMBER = PRICING.GST.NUMBER;
  const GST_RATE = PRICING.GST.RATE;

  // If no payment data is present, redirect to home
  React.useEffect(() => {
    if (!paymentData.paymentId) {
      navigate('/');
      return;
    }

    if (!purchaseTrackedRef.current) {
      purchaseTrackedRef.current = true;
      trackPixelPurchase({
        value: paymentData.amountPaid || paymentData.amount || 0,
        planName: paymentData.plan,
        paymentId: paymentData.paymentId,
        subscriptionId: paymentData.subscriptionId
      });
    }

    // Save receipt to database when payment is successful
    saveReceiptToDatabase();
  }, [paymentData, navigate]);
  
  // Use the calculateGstComponents function from config
  
  // Get the payment amount from paymentData or use a default
  const getPaymentAmount = () => {
    // Use the actual paid amount that was passed from PricingAndPlans.js
    // This will be the discounted amount if a coupon was applied
    // For monthly plans with extreme discounts, handle specifically
    if (isMonthlyPlan() && paymentData.amountPaid) {
      const originalAmount = getOriginalAmount();
      // Check if extremely discounted using the helper function
      if (isExtremelyDiscounted(originalAmount, paymentData.amountPaid)) {
        return paymentData.amountPaid;
      }
    }
    
    // For regular cases
    const actualPaidAmount = paymentData.amountPaid || paymentData.amount || 0;
    return actualPaidAmount;
  };
  
  // Get the original amount before any discounts
  const getOriginalAmount = () => {
    // For monthly plans, ensure we're using the monthly price (not yearly)
    if (isMonthlyPlan()) {
      return paymentData.originalAmount || PRICING.PLANS.MONTHLY.BASE_PRICE; // Default monthly price if not specified
    }
    return paymentData.originalAmount || paymentData.amount || 0;
  };
  
  // Check if a discount was applied
  const hasDiscount = () => {
    // For monthly plans with extreme discounts
    if (isMonthlyPlan() && paymentData.amountPaid) {
      const originalAmount = getOriginalAmount();
      if (isExtremelyDiscounted(originalAmount, paymentData.amountPaid)) {
        return true;
      }
    }
    
    return paymentData.hasDiscount || 
           (paymentData.amountPaid && paymentData.originalAmount && 
            paymentData.amountPaid < paymentData.originalAmount);
  };
  
  // Get the discount amount if applicable
  const getDiscountAmount = () => {
    if (hasDiscount()) {
      // For monthly plans with extreme discounts
      if (isMonthlyPlan() && paymentData.amountPaid) {
        const originalAmount = getOriginalAmount();
        if (isExtremelyDiscounted(originalAmount, paymentData.amountPaid)) {
          return originalAmount - paymentData.amountPaid;
        }
      }
      
      return paymentData.discountAmount || 
             (paymentData.originalAmount - paymentData.amountPaid) || 0;
    }
    return 0;
  };
  
  // Check if this is a monthly plan
  const isMonthlyPlan = () => {
    return paymentData.plan && paymentData.plan.toLowerCase().includes('monthly');
  };
  
  // Save receipt to database
  const saveReceiptToDatabase = async () => {
    if (!paymentData.paymentId || receiptSaved) return;
    
    try {
      const apiBaseUrl = config.getApiUrl();
      const amount = getPaymentAmount();
      const gstComponents = calculateGstComponents(amount);
      
      const receiptData = {
        subscriptionId: paymentData.subscriptionId,
        paymentId: paymentData.paymentId,
        customerName: paymentData.name,
        customerEmail: paymentData.email,
        customerPhone: paymentData.phone,
        planName: paymentData.plan,
        amount: amount,
        baseAmount: gstComponents.baseAmount,
        gstAmount: gstComponents.gstAmount,
        gstRate: GST_RATE,
        gstNumber: GST_NUMBER,
        isMonthlyPlan: isMonthlyPlan(),
        receiptDate: new Date().toISOString()
      };
      
      // Use API key authentication instead of token authentication
      const apiKey = process.env.REACT_APP_API_KEY;
      const response = await fetch(`${apiBaseUrl}/api/receipts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify(receiptData)
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setReceiptSaved(true);
        } else {
          setReceiptError('Failed to save receipt');
        }
      } else {
        setReceiptError('Failed to save receipt');
      }
    } catch (error) {
      console.error('Error saving receipt:', error);
      setReceiptError('Error saving receipt');
    }
  };
  
  // Generate PDF receipt
  const generateReceipt = () => {
    const doc = new jsPDF();
    const amount = getPaymentAmount();
    const gstComponents = calculateGstComponents(amount);
    
    // Add header
    doc.setFontSize(20);
    doc.setTextColor(...PRICING.BRAND_COLORS.PRIMARY); // YoungPresso orange
    doc.text("YoungPresso ( A Mettlesome Product )", 105, 20, { align: "center" });
    
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Payment Receipt", 105, 30, { align: "center" });
    
    // Add receipt details
    doc.setFontSize(10);
    doc.text("Receipt Date: " + new Date().toLocaleDateString(), 15, 40);
    doc.text("Receipt No: REC-" + paymentData.paymentId.slice(-6), 15, 45);
    doc.text("GST No: " + GST_NUMBER, 15, 50);
    
    // Customer info
    doc.setFontSize(12);
    doc.text("Customer Information", 15, 60);
    doc.line(15, 62, 195, 62);
    
    doc.setFontSize(10);
    doc.text("Name: " + (paymentData.name || 'N/A'), 15, 70);
    doc.text("Email: " + (paymentData.email || 'N/A'), 15, 75);
    doc.text("Phone: " + (paymentData.phone || 'N/A'), 15, 80);
    
    // Payment details
    doc.setFontSize(12);
    doc.text("Payment Details", 15, 90);
    doc.line(15, 92, 195, 92);
    
    // Create table for payment details
    const tableData = [
      ["Plan", paymentData.plan || 'N/A'],
      ["Payment ID", paymentData.paymentId || 'N/A'],
      ["Subscription ID", paymentData.subscriptionId || 'N/A'],
      ["Payment Date", new Date().toLocaleDateString()]
    ];
    
    autoTable(doc, {
      startY: 95,
      head: [["Item", "Details"]],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [255, 107, 53] }
    });
    
    // Add GST breakdown
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.text("Amount Breakdown", 15, finalY);
    doc.line(15, finalY + 2, 195, finalY + 2);
    
    // Create a plan description without attaching period to the amount
    const planDescription = "Base Amount" + (isMonthlyPlan() ? " (Monthly Plan)" : " (Yearly Plan)");
    
    // Use the discount information passed from PricingAndPlans.js
    const isDiscounted = hasDiscount();
    const discountAmount = getDiscountAmount();
    const originalAmount = getOriginalAmount();
    
    // Create the receipt body rows
    const receiptRows = [
      [planDescription, "Rs. " + gstComponents.baseAmount.toFixed(2)],
      ["GST (" + GST_RATE + "%)", "Rs. " + gstComponents.gstAmount.toFixed(2)],
    ];
    
    // Add discount information if applicable
    if (isDiscounted) {
      const couponInfo = paymentData.couponCode ? ` (${paymentData.couponCode})` : '';
      receiptRows.push([`Coupon Discount${couponInfo}`, "- Rs. " + discountAmount.toFixed(2)]);
    }
    
    // Add total amount row - make sure we're showing the actual paid amount
    // For monthly plans with extreme discounts, show the exact amount paid
    if (isMonthlyPlan() && isDiscounted && paymentData.amountPaid) {
      const originalAmount = getOriginalAmount();
      if (isExtremelyDiscounted(originalAmount, paymentData.amountPaid)) {
        receiptRows.push(["Total Amount", "Rs. " + paymentData.amountPaid.toFixed(2)]);
      } else {
        receiptRows.push(["Total Amount", "Rs. " + gstComponents.totalAmount.toFixed(2)]);
      }
    } else {
      receiptRows.push(["Total Amount", "Rs. " + gstComponents.totalAmount.toFixed(2)]);
    }
    
    // Add monthly subscription note if applicable
    if (isMonthlyPlan()) {
      receiptRows.push(["Note: This is a monthly subscription. You will be charged this amount every month.", ""]);
    }
    
    autoTable(doc, {
      startY: finalY + 5,
      head: [["Description", "Amount (₹)"]],
      body: receiptRows,
      theme: 'grid',
      headStyles: { fillColor: [255, 107, 53] }
    });
    
    // Add footer
    const gstFinalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : finalY + 60;
    doc.setFontSize(10);
    doc.text("Thank you for subscribing to YoungPresso!", 105, gstFinalY, { align: "center" });
    doc.text("For any queries, please contact support@youngpresso.com", 105, gstFinalY + 5, { align: "center" });
    
    return doc;
  };
  
  // Handle download receipt
  const handleDownloadReceipt = () => {
    setIsGeneratingReceipt(true);
    try {
      const doc = generateReceipt();
      doc.save("YoungPresso_Receipt_" + (paymentData.paymentId ? paymentData.paymentId.slice(-6) : 'unknown') + ".pdf");
    } catch (error) {
      console.error('Error generating receipt:', error);
    } finally {
      setIsGeneratingReceipt(false);
    }
  };

  if (!paymentData.paymentId) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f5f5f5',
        py: 4,
        px: 2
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            borderRadius: 2,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ mb: 3 }}>
            <CheckCircleIcon
              sx={{
                fontSize: 80,
                color: '#4CAF50',
                animation: 'scaleIn 0.5s ease-out',
                '@keyframes scaleIn': {
                  from: {
                    transform: 'scale(0)',
                    opacity: 0
                  },
                  to: {
                    transform: 'scale(1)',
                    opacity: 1
                  }
                }
              }}
            />
          </Box>

          <Typography variant="h4" component="h1" sx={{ mb: 2, color: '#4CAF50', fontWeight: 600 }}>
            Payment Successful!
          </Typography>

          {paymentData.verificationPending && (
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              Your payment was received. Confirmation is being processed — you will receive a receipt by email shortly.
            </Alert>
          )}
          
          {/* <Typography 
            sx={{ 
              mb: 4, 
              px: 3,
              py: 2,
              color: '#FF6B35', 
              fontFamily: '"Comic Sans MS", cursive, sans-serif',
              fontSize: '1.1rem',
              fontWeight: 'bold',
              fontStyle: 'italic',
              textAlign: 'center',
              borderRadius: 2,
              backgroundColor: 'rgba(255, 107, 53, 0.1)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              maxWidth: '90%',
              mx: 'auto'
            }}
          >
            🎉 Congratulations and Welcome to YoungPresso Family! 
          </Typography> */}
          
          <Button
            variant="outlined"
            color="primary"
            onClick={handleDownloadReceipt}
            disabled={isGeneratingReceipt}
            startIcon={isGeneratingReceipt ? <CircularProgress size={20} /> : <ReceiptIcon />}
            sx={{ mb: 4 }}
          >
            {isGeneratingReceipt ? 'Generating...' : 'Download Receipt'}
          </Button>

          <Box sx={{ mb: 4 }}>
            {[
              { label: 'Payment ID', value: paymentData.paymentId },
              { label: 'Subscription ID', value: paymentData.subscriptionId },
              { label: 'Name', value: paymentData.name },
              { label: 'Email', value: paymentData.email },
              { label: 'Phone', value: paymentData.phone },
              { label: 'Selected Plan', value: paymentData.plan },
             
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 1.5,
                  borderBottom: index === 6 ? 'none' : '1px solid #eee'
                }}
              >
                <Typography sx={{ color: '#666', fontWeight: 500 }}>{item.label}:</Typography>
                <Typography sx={{ color: '#333', fontWeight: 600 }}>{item.value || 'N/A'}</Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              pt: 3,
              mt: 3,
              borderTop: '1px solid #eee',
              textAlign: 'center'
            }}
          >
            <Typography sx={{ color: '#666', mb: 1 }}>
              You will shortly receive a Welcome Email at your Registered Email id from
            </Typography>
            <Typography sx={{ color: '#1976D2', fontWeight: 600, mb: 3 }}>
              info@youngpresso.com
            </Typography>
            
            {receiptError && (
              <Typography sx={{ color: 'error.main', mb: 2 }}>
                {receiptError}
              </Typography>
            )}
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/')}
                sx={{ mt: 2 }}
              >
                Go to Home
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
};

export default PaymentSuccess;